import React from 'react';

import { SKELETON } from '../../theme/tokens';
import { Block } from './Skeleton.styles';

export type SkeletonProps = {
  /** Width — number → px, string → used verbatim (e.g. `'60%'`). Defaults to full width. */
  width?: number | string;
  /** Height — number → px, string → used verbatim. Defaults to `'1em'` (one text line). */
  height?: number | string;
  /** Corner radius — number → px, string → used verbatim. Ignored when `circle`. */
  radius?: number | string;
  /** Render a perfect circle (radius 50%) — pair with equal width/height (e.g. a gauge ring). */
  circle?: boolean;
  /** Lay out as a block instead of inline (chart / full-width placeholders). */
  block?: boolean;
  /** Style hook for spacing/positioning from the consuming `.styles.ts`. */
  className?: string;
};

const toCss = (value: number | string | undefined, fallback: string): string =>
  value === undefined ? fallback : typeof value === 'number' ? `${value}px` : value;

/**
 * A neutral placeholder block with a very subtle sheen that stands in for a single
 * figure / number / chart while its data loads. Decorative only (`aria-hidden`) —
 * the surrounding labels carry the meaning, so a screen reader skips it. Pair it
 * with `useMockEndpoint`'s `loading`:
 *
 *   {loading ? <Skeleton width={72} height={26} /> : <Figure value={…} />}
 *
 * Size it to the box the real value occupies so the layout doesn't jump on reveal.
 */
export const Skeleton = ({
  width,
  height,
  radius,
  circle = false,
  block = false,
  className,
}: SkeletonProps): React.ReactElement => (
  <Block
    aria-hidden="true"
    className={className}
    $w={toCss(width, '100%')}
    $h={toCss(height, '1em')}
    $radius={circle ? '50%' : toCss(radius, `${SKELETON.RADIUS_PX}px`)}
    $block={block}
  />
);

export default Skeleton;
